import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";

import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";

import { regexSnowflakeField } from "../../../../../../util/regularExpress";

const Details = ({ fields, currentField, detailFormData, newEntity, validateAttributeName }) => {
  const attributeDataTypes = useSelector((state) => state.config.attributeDataTypes);

  const types = useMemo(
    () => [
      {
        value: "NoOption",
        text: "Select Type",
      },
      ...attributeDataTypes
        .filter((option) => option.type !== "Reference")
        .map((option) => ({
          value: option.type,
          text: option.type,
          options: option.type !== "Date" && option.type !== "DateTime" && option.type !== "Time" ? option.options : [],
        })),
    ],
    [attributeDataTypes]
  );

  const [lengthList, setLengthList] = useState([]);
  const detailFormDataRef = useRef(detailFormData);

  const handleAttribute = (str, setData) => {
    const isValid = str.length !== 0 ? regexSnowflakeField(str) : true;
    if (isValid) setData(str);
  };

  //Get Length Options from selected values of "Type" Dropdown
  const getLengthOptions = useCallback(
    (selectedDataType, selectedLength) => {
      detailFormDataRef.current.setLength("NoOption");
      detailFormDataRef.current.setOpenLengthList(false);

      if (types.length > 0) {
        let lengthArr = [{ value: "NoOption", text: "Select Length" }];

        types.forEach((row) => {
          if (selectedDataType === row.value && row.options && row.options.length > 0) {
            row.options.forEach((option) => {
              lengthArr.push({ value: option.value, text: option.value });

              if (currentField && selectedLength && option.value === selectedLength) {
                detailFormDataRef.current.setLength(selectedLength);
                detailFormDataRef.current.setOldLength(selectedLength);
              }
            });

            detailFormDataRef.current.setOpenLengthList(true);
          }
        });
        setLengthList(lengthArr);
      }
    },
    [currentField, types]
  );

  useEffect(() => {
    const tField = fields.find((field) => field.name === currentField);

    if (tField) {
      detailFormDataRef.current.setAttribute(tField.name);
      types.forEach((option) => {
        if (currentField && tField.type && option.value === tField.type) detailFormDataRef.current.setType(tField.type);
      });
      detailFormDataRef.current.setDescription(tField.description);
    }
  }, [currentField, fields, types]);

  useEffect(() => {
    if (currentField) {
      const tField = fields.find((field) => field.name === currentField);
      if (tField) getLengthOptions(tField.type, tField.length);
    }
  }, [currentField, fields, getLengthOptions]);

  return (
    <Box sx={{ width: "100%", height: "auto", marginTop: "20px" }} role="presentation">
      <>
        <TextField
          required
          autoFocus
          error={!!detailFormData.attributeError}
          helperText={detailFormData.attributeError}
          value={detailFormData.attribute}
          inputProps={{ maxLength: 50 }}
          onBlur={(e) => detailFormDataRef.current.setAttributeError(validateAttributeName(e.target.value))}
          onChange={(e) => handleAttribute(e.target.value, detailFormData.setAttribute)}
          fullWidth
          label="Attribute"
          variant="outlined"
          sx={{ my: 2 }}
        />
        <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={detailFormData.type}
            onChange={(e) => {
              detailFormData.setType(e.target.value);
              getLengthOptions(e.target.value);
            }}
            label="Type"
            disabled={!(newEntity || (!newEntity && !currentField))}
          >
            {types.map((type) => (
              <MenuItem key={type.value} value={type.value} disabled={type.value === "NoOption"}>
                {type.text}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {detailFormData.openLengthList && (
          <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
            <InputLabel>Length</InputLabel>
            <Select
              value={detailFormData.length}
              onChange={(e) => detailFormData.setLength(e.target.value)}
              label="Length"
              disabled={
                !(newEntity || (!newEntity && !currentField)) &&
                detailFormData.type !== "String" &&
                detailFormData.type !== "Char"
              }
            >
              {lengthList.map((type) => (
                <MenuItem
                  key={type.value}
                  value={type.value}
                  disabled={
                    type.value === "NoOption" ||
                    (type.value !== "NoOption" &&
                      detailFormData.oldLength !== "NoOption" &&
                      type.value < detailFormData.oldLength)
                  }
                >
                  {type.text}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <TextField
          value={detailFormData.description}
          inputProps={{ maxLength: 120 }}
          onChange={(e) => detailFormData.setDescription(e.target.value)}
          fullWidth
          multiline
          label="Description"
          variant="outlined"
          sx={{ my: 2 }}
        />
      </>
    </Box>
  );
};

export default Details;
